import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { adminPresencasService, equipeService, modalidadeService, turmaService, unidadeService } from '../../services/api';
import AdminPresencasDetalheDrawer from './AdminPresencasDetalheDrawer';
import '../../styles/AdminPresencas.css';

dayjs.locale('pt-br');

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_LABELS = {
  REALIZADA: { texto: 'Realizada', cor: 'green' },
  PENDENTE: { texto: 'Pendente', cor: 'orange' },
  FUTURA: { texto: 'Futura', cor: 'blue' },
  EM_ANDAMENTO: { texto: 'Em andamento', cor: 'processing' },
  CANCELADA: { texto: 'Cancelada', cor: 'red' },
};

const PAGE_SIZE_PADRAO = 25;

const filtrosIniciais = () => ({
  periodo: [dayjs().startOf('month'), dayjs().endOf('month')],
  unidadeId: undefined,
  modalidadeId: undefined,
  turmaId: undefined,
  equipeId: undefined,
  statusChamada: undefined,
});

const AdminPresencasPage = () => {
  const [filtros, setFiltros] = useState(filtrosIniciais());
  const [unidades, setUnidades] = useState([]);
  const [modalidades, setModalidades] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [professores, setProfessores] = useState([]);

  const [items, setItems] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_PADRAO);
  const [total, setTotal] = useState(0);
  const [ordenarPor, setOrdenarPor] = useState('data');
  const [direcao, setDirecao] = useState('desc');

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [encontroSelecionado, setEncontroSelecionado] = useState(null);

  useEffect(() => {
    unidadeService.list().then((r) => setUnidades(r.data || [])).catch(() => setUnidades([]));
    modalidadeService.list().then((r) => setModalidades(r.data || [])).catch(() => setModalidades([]));
    turmaService.list().then((r) => setTurmas(r.data || [])).catch(() => setTurmas([]));
    equipeService.list().then((r) => setProfessores(r.data || [])).catch(() => setProfessores([]));
  }, []);

  const carregarChamadas = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const [dataInicio, dataFim] = filtros.periodo || [];
      const response = await adminPresencasService.listarChamadas({
        dataInicio: dataInicio ? dataInicio.format('YYYY-MM-DD') : undefined,
        dataFim: dataFim ? dataFim.format('YYYY-MM-DD') : undefined,
        unidadeId: filtros.unidadeId,
        modalidadeId: filtros.modalidadeId,
        turmaId: filtros.turmaId,
        equipeId: filtros.equipeId,
        statusChamada: filtros.statusChamada,
        page,
        pageSize,
        ordenarPor,
        direcao,
      });
      const dados = response.data || {};
      setItems(dados.items || []);
      setTotal(dados.total || 0);
      setResumo(dados.resumo || null);
    } catch (error) {
      setErro('Não foi possível carregar as chamadas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filtros, page, pageSize, ordenarPor, direcao]);

  useEffect(() => {
    carregarChamadas();
  }, [carregarChamadas]);

  const atualizarFiltro = (campo, valor) => {
    setPage(1);
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => {
    setPage(1);
    setFiltros(filtrosIniciais());
  };

  const handleTableChange = (paginacao, _filters, sorter) => {
    if (paginacao.current !== page) setPage(paginacao.current);
    if (paginacao.pageSize !== pageSize) {
      setPageSize(paginacao.pageSize);
      setPage(1);
    }
    if (sorter && sorter.field && sorter.columnKey) {
      const campoOrdenavel = {
        data: 'data',
        unidade: 'unidade',
        modalidade: 'modalidade',
        turma: 'turma',
        professor: 'professor',
        status_chamada: 'status',
        percentual_presenca: 'percentual_presenca',
      }[sorter.columnKey];
      if (campoOrdenavel) {
        setOrdenarPor(campoOrdenavel);
        setDirecao(sorter.order === 'ascend' ? 'asc' : 'desc');
      }
    }
  };

  const opcoesUnidade = useMemo(() => unidades.map((u) => ({ value: u.id, label: u.nome_fantasia || u.nome })), [unidades]);
  const opcoesModalidade = useMemo(() => modalidades.map((m) => ({ value: m.id, label: m.nome })), [modalidades]);
  const opcoesTurma = useMemo(() => turmas.map((t) => ({ value: t.id, label: t.nome || t.nome_turm })), [turmas]);
  const opcoesProfessor = useMemo(() => professores.map((p) => ({ value: p.id, label: p.nome })), [professores]);
  const opcoesStatus = useMemo(
    () => Object.entries(STATUS_LABELS).map(([value, { texto }]) => ({ value, label: texto })),
    []
  );

  const colunas = [
    {
      title: 'Data',
      key: 'data',
      dataIndex: 'data',
      sorter: true,
      render: (valor) => (valor ? dayjs(valor).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Horário',
      key: 'horario',
      render: (_, record) => `${String(record.hora_inicio).slice(0, 5)}–${String(record.hora_fim).slice(0, 5)}`,
    },
    { title: 'Unidade', key: 'unidade', dataIndex: ['unidade', 'nome'], sorter: true },
    { title: 'Modalidade', key: 'modalidade', dataIndex: ['modalidade', 'nome'], sorter: true },
    { title: 'Turma', key: 'turma', dataIndex: ['turma', 'nome'], sorter: true },
    { title: 'Professor', key: 'professor', dataIndex: ['professor', 'nome'], sorter: true },
    { title: 'Alunos', key: 'total_alunos', dataIndex: 'total_alunos', align: 'center' },
    { title: 'Presentes', key: 'presentes', dataIndex: 'presentes', align: 'center' },
    { title: 'Ausentes', key: 'ausentes', dataIndex: 'ausentes', align: 'center' },
    {
      title: 'Frequência',
      key: 'percentual_presenca',
      dataIndex: 'percentual_presenca',
      sorter: true,
      render: (valor) => (valor === null || valor === undefined ? '—' : `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 1 })}%`),
    },
    {
      title: 'Status',
      key: 'status_chamada',
      dataIndex: 'status_chamada',
      sorter: true,
      render: (valor) => {
        const info = STATUS_LABELS[valor] || { texto: valor, cor: 'default' };
        return <Tag color={info.cor}>{info.texto}</Tag>;
      },
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <Button size="small" onClick={() => setEncontroSelecionado(record.encontro_id)}>
          Visualizar chamada
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-presencas-page">
      <Title level={2} className="admin-presencas-page__titulo">
        Presenças
      </Title>
      <Text type="secondary" className="admin-presencas-page__subtitulo">
        Acompanhe as chamadas e a frequência dos alunos.
      </Text>

      <Row gutter={[16, 16]} className="admin-presencas-page__cards">
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Chamadas realizadas" value={resumo?.chamadas_realizadas ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Chamadas pendentes" value={resumo?.chamadas_pendentes ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Aulas futuras" value={resumo?.aulas_futuras ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Taxa de presença"
              value={resumo?.taxa_presenca ?? undefined}
              precision={1}
              suffix={resumo?.taxa_presenca !== null && resumo?.taxa_presenca !== undefined ? '%' : ''}
              formatter={(value) => (value === undefined ? '—' : value)}
            />
          </Card>
        </Col>
      </Row>

      <Card className="admin-presencas-page__filtros" bordered={false}>
        <Space wrap size="middle" role="group" aria-label="Filtros de chamadas">
          <RangePicker
            value={filtros.periodo}
            onChange={(valor) => atualizarFiltro('periodo', valor)}
            format="DD/MM/YYYY"
            aria-label="Período"
          />
          <Select
            placeholder="Unidade"
            allowClear
            style={{ minWidth: 160 }}
            options={opcoesUnidade}
            value={filtros.unidadeId}
            onChange={(valor) => atualizarFiltro('unidadeId', valor)}
            aria-label="Unidade"
          />
          <Select
            placeholder="Modalidade"
            allowClear
            style={{ minWidth: 160 }}
            options={opcoesModalidade}
            value={filtros.modalidadeId}
            onChange={(valor) => atualizarFiltro('modalidadeId', valor)}
            aria-label="Modalidade"
          />
          <Select
            placeholder="Turma"
            allowClear
            style={{ minWidth: 160 }}
            options={opcoesTurma}
            value={filtros.turmaId}
            onChange={(valor) => atualizarFiltro('turmaId', valor)}
            aria-label="Turma"
          />
          <Select
            placeholder="Professor"
            allowClear
            style={{ minWidth: 160 }}
            options={opcoesProfessor}
            value={filtros.equipeId}
            onChange={(valor) => atualizarFiltro('equipeId', valor)}
            aria-label="Professor"
          />
          <Select
            placeholder="Status da chamada"
            allowClear
            style={{ minWidth: 180 }}
            options={opcoesStatus}
            value={filtros.statusChamada}
            onChange={(valor) => atualizarFiltro('statusChamada', valor)}
            aria-label="Status da chamada"
          />
          <Button onClick={limparFiltros}>Limpar filtros</Button>
        </Space>
      </Card>

      {loading && (
        <div role="status" aria-live="polite" className="admin-presencas-page__loading">
          <Text type="secondary">Carregando chamadas…</Text>
          <Skeleton active paragraph={{ rows: 5 }} style={{ marginTop: 12 }} />
        </div>
      )}

      {!loading && erro && (
        <Card className="admin-presencas-page__erro">
          <Text>{erro}</Text>
          <div className="admin-presencas-page__erro-acoes">
            <Button type="primary" onClick={carregarChamadas}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      )}

      {!loading && !erro && items.length === 0 && (
        <Empty description="Nenhuma chamada encontrada para os filtros selecionados." />
      )}

      {!loading && !erro && items.length > 0 && (
        <Table
          className="admin-presencas-page__tabela"
          rowKey="encontro_id"
          columns={colunas}
          dataSource={items}
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
          }}
        />
      )}

      <AdminPresencasDetalheDrawer
        encontroId={encontroSelecionado}
        onClose={() => setEncontroSelecionado(null)}
      />
    </div>
  );
};

export default AdminPresencasPage;
